import { Match } from 'effect';
import { compact, last } from 'lodash';
import moment from 'moment';
import { atomFamily, selectorFamily } from 'recoil';

import { type AsOfDateSetting, CORRELATION_BLOCK_PRO_FORMA_METRICS, type Indices } from 'venn-utils';
import {
  ALLOCATION_PANEL_AS_OF_DATE_DATE_FORMAT,
  CURRENT_AS_OF_DATE,
  FREQUENCY_TO_PERIOD,
  HISTORICAL_BENCHMARK_BLOCKS,
  isHistoricalSubject,
  PRIVATES_ASSET_GROWTH_BLOCKS,
  PRO_FORMA_HISTORICAL_AS_OF_DATE_PARAMETERIZED_BLOCKS,
  PRO_FORMA_HISTORICAL_BLOCKS,
  PRO_FORMA_HISTORICAL_METRICS,
} from 'venn-utils';
import { rangeDebugQuery } from '../async/rangeDebug';
import type { BlockId, Subject } from '../types';
import { blockMetrics } from './blockConfig';
import { blockSettings } from './blockSettings';
import { blockDateRange } from './dateRange';
import { requestSubjects } from './subjects';

export type BlockAsOfDateSetting = [AsOfDateSetting, AsOfDateSetting];
export type BlockAsOfDateSettingIndices = Indices<BlockAsOfDateSetting>;
export type BlockAsOfDateSettingCount = BlockAsOfDateSetting['length'];

/**
 * Stores selected as of date setting for blocks that allow users to pick
 * a specific as of date for historical portfolios (Portfolio Composition and Portfolio Comparison blocks).
 */
export const blockAsOfDateSetting = atomFamily<BlockAsOfDateSetting, BlockId>({
  key: 'blockAsOfDateSetting',
  default: [CURRENT_AS_OF_DATE, CURRENT_AS_OF_DATE],
});

export const allSubjectsAsOfDates = selectorFamily<
  { blockId: BlockId; subject: Subject; asOfDate: number | undefined }[],
  [BlockId, Subject][]
>({
  key: 'allSubjectsAsOfDates',
  get:
    (pairs) =>
    ({ get }) =>
      pairs.map(([blockId, subject]) => ({
        blockId,
        subject,
        asOfDate: get(subjectAsOfDate([blockId, subject])),
      })),
});

/**
 * Returns "as of date" given a pair of [BlockId, Subject]
 */
export const subjectAsOfDate = selectorFamily<number | undefined, [BlockId, Subject | undefined]>({
  key: 'subjectAsOfDate',
  get:
    ([blockId, subject]) =>
    ({ get }) => {
      return get(subjectAsOfDateWithExplicitSubjectIndex([blockId, subject, 0]));
    },
});

export const subjectAsOfDateWithExplicitSubjectIndex = selectorFamily<
  number | undefined,
  [BlockId, Subject | undefined, BlockAsOfDateSettingIndices]
>({
  key: 'subjectAsOfDate',
  get:
    ([blockId, subject, subjectIndex]) =>
    ({ get }) => {
      if (!subject) {
        return undefined;
      }

      const requestSubject = get(requestSubjects([subject]))[0]!;

      // if the subject is not historical portfolio it doesn't have a concept of "as of date"
      if (!isHistoricalSubject(requestSubject)) {
        return undefined;
      }

      const isProForma = get(isProFormaBlockOrMetric(blockId));

      // If block or it's selected metrics don't treat historical portfolio as pro-forma then there is no "as of date"
      if (!isProForma) {
        return undefined;
      }

      const setting = get(blockSettings(blockId));
      const rangeDebug = get(rangeDebugQuery(subject));

      const selectedMetric = get(blockMetrics(blockId));
      const isCorrelationProForma = selectedMetric.find((metric) =>
        CORRELATION_BLOCK_PRO_FORMA_METRICS.includes(metric),
      );
      if (isCorrelationProForma) {
        const dateRange = get(blockDateRange(blockId));
        return dateRange?.range.to;
      }

      // For public-private growth simulation blocks the as of date is the minimum
      // between latest drifted allocations date and end of previous quarter
      const isPublicPrivateGrowthBlock = PRIVATES_ASSET_GROWTH_BLOCKS.includes(setting.customBlockType);
      if (isPublicPrivateGrowthBlock) {
        const dateRange = get(blockDateRange(blockId));
        const endOfPreviousQuarter = moment.utc(dateRange?.range.to).subtract(1, 'quarter').endOf('quarter').valueOf();
        return Math.min(...compact([endOfPreviousQuarter, rangeDebug?.maxEndDate]));
      }

      // If block has explicit as of date setting we just read from it and return back
      // At the moment only Portfolio Composition block has this setting
      const hasExplicitAsOfDateSetting = PRO_FORMA_HISTORICAL_AS_OF_DATE_PARAMETERIZED_BLOCKS.includes(
        setting.customBlockType,
      );
      if (hasExplicitAsOfDateSetting) {
        const period = rangeDebug?.maxFrequency ? FREQUENCY_TO_PERIOD[rangeDebug?.maxFrequency] : 'day';
        const dates = rangeDebug?.historicalPortfolioMetadata?.allSortedAllocationDates;
        const lastUploaded = dates?.length ? moment.utc(last(dates)).endOf(period).valueOf() : undefined;
        const blockAsOfDate = get(blockAsOfDateSetting(blockId))[subjectIndex];
        return Match.value(blockAsOfDate).pipe(
          Match.tag('Current', () => rangeDebug?.maxEndDate),
          Match.tag('LastUploaded', () => lastUploaded),
          Match.tag('Date', ({ date }) =>
            moment.utc(date, ALLOCATION_PANEL_AS_OF_DATE_DATE_FORMAT).endOf('day').valueOf(),
          ),
          Match.exhaustive,
        );
      }

      // For all other blocks (e.g. Correlations, Sensitivity Analysis, Forecast metrics in Perf & Risk)
      // we just always return current (meaning latest drifted) allocations
      return rangeDebug?.maxEndDate;
    },
});

/**
 * Returns true if block or one of it's selected metrics treat historical portfolio as pro-forma
 * Examples:
 * - Sensitivity Analysis block
 * - Forecast metrics in Performance & Risk block
 */
export const isProFormaBlockOrMetric = selectorFamily<boolean, BlockId>({
  key: 'isProFormaBlockOrMetric',
  get:
    (blockId) =>
    ({ get }) => {
      const setting = get(blockSettings(blockId));
      const selectedMetrics = get(blockMetrics(blockId));
      const customBlockType = setting.customBlockType;
      const isProFormaBlock = customBlockType && PRO_FORMA_HISTORICAL_BLOCKS.includes(customBlockType);
      const isProFormaMetric = selectedMetrics?.some((metric) => PRO_FORMA_HISTORICAL_METRICS.includes(metric));

      return isProFormaBlock || isProFormaMetric;
    },
});

export const isProFormaBlockOrMetricBenchmark = selectorFamily<boolean, BlockId>({
  key: 'isProFormaBlockOrMetric',
  get:
    (blockId) =>
    ({ get }) => {
      const setting = get(blockSettings(blockId));
      const customBlockType = setting.customBlockType;
      const isProFormaBlock = get(isProFormaBlockOrMetric(blockId));
      const isProFormaBenchmark = !HISTORICAL_BENCHMARK_BLOCKS.includes(customBlockType);
      return isProFormaBlock && isProFormaBenchmark;
    },
});

export const allocatorAsOfDateSetting = atomFamily<AsOfDateSetting, Subject | undefined>({
  key: 'allocatorAsOfDateSetting',
  default: CURRENT_AS_OF_DATE,
});
